import {
  memo,
  useState,
  useRef,
  useCallback,
  useEffect,
  type ReactNode,
} from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import { hasVisibleTitle, getTitleWithFallback } from "@/shared/utils/general/titleUtils"
import type { DbObject } from "@/db/schema"
import { updateTaskTitle, deleteTask, addTask } from "@/db/operations"
import { archiveSection } from "@/features/project-header/projectOps"
import { SectionArchiveDialog } from "./SectionArchiveDialog"
import DotsIcon from "@/shared/icons/totodoicons/stroke/three-dots-horizontal.svg?react"
import DeleteIcon from "@/shared/icons/totodoicons/stroke/delete.svg?react"
import CheckIcon from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"
import PlusIcon from "@/shared/icons/totodoicons/stroke/plus.svg?react"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { useEditFocusStore } from "@/features/task-detail/stores/editFocusStore"
import { ObjectStatus } from "@/db/schema"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { enhancedSmoothScroll } from "@/shared/utils/ui/enhancedSmoothScroll"
import { useMenuPosition } from "@/shared/hooks/useMenuPosition"
// Types
// ---------------------------------------------------------------------------

interface SectionRowProps {
  task: DbObject
  className?: string
  /** When true, content is scaled to 80% while keeping the same box height.
   *  Used in container-grouped views where ContainerGroupHeader already provides structure. */
  compact?: boolean
  /** Ref callback for the drag handle element (from useSortable.setActivatorNodeRef) */
  dragHandleRef?: (node: HTMLElement | null) => void
  /** Event listeners for the drag handle (from useSortable.listeners) */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  dragListeners?: Record<string, Function>
  /** Selection state */
  isSelected?: boolean
  /** Whether multi-select mode is active (any task selected) */
  isSelectionMode?: boolean
  /** Called when user taps the selection icon */
  onSelect?: () => void
  /** IDs of tasks under this section (positionally — between this and next section) */
  childIds?: string[]
  /** Whether this section is the first item in its container/group — no top spacing */
  isFirstInGroup?: boolean
  /** Optional content rendered inside the inner (selected) area, e.g. DragCounterBadge */
  children?: ReactNode
  /** Open a task's detail panel (for insert-and-edit flow) */
  onOpen?: (id: string, editTitle?: boolean) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Section row — section divider within a task list.
 *
 * v1 pattern: CardPreviewSection.
 * Renders as a bold text label with a top border separator.
 * Double-click (desktop) / double-tap (mobile) enters inline edit mode.
 * Context menu on right-click: Delete.
 */
export const SectionRow = memo(function SectionRow({
  task,
  className,
  compact,
  dragHandleRef,
  dragListeners,
  isSelected,
  isSelectionMode,
  onSelect,
  childIds,
  // isFirstInGroup is consumed by the parent (SectionBlock) for spacing
  isFirstInGroup: _isFirstInGroup,
  children,
  onOpen,
}: SectionRowProps) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 })
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const editRef = useRef<HTMLSpanElement>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuStyle = useMenuPosition(menuPos, menuRef)
  const requestEditFocus = useEditFocusStore((s) => s.requestEditFocus)

  const { isMobile } = useBreakpoint()
  const isArchived =
    task.status === ObjectStatus.Completed ||
    task.status === ObjectStatus.Canceled

  // Auto-focus span when entering edit mode
  useEffect(() => {
    if (isEditing && editRef.current) {
      editRef.current.focus({ preventScroll: true })
      // Place cursor at end without selecting text
      const range = document.createRange()
      const sel = window.getSelection()
      if (editRef.current.childNodes.length > 0) {
        range.setStartAfter(editRef.current.lastChild!)
      } else {
        range.setStart(editRef.current, 0)
      }
      range.collapse(true)
      sel?.removeAllRanges()
      sel?.addRange(range)
      // Scroll section into view if partially hidden.
      // Delay to let the virtualizer / layout settle after the re-render.
      const el = rowRef.current
      if (el) {
        const id = setTimeout(() => {
          enhancedSmoothScroll(el, {
            block: "nearest",
            duration: 300,
            bottomInset: 100,
          })
        }, 1000)
        return () => clearTimeout(id)
      }
    }
  }, [isEditing])

  // Close context menu on click outside
  useEffect(() => {
    if (!showMenu) return
    const handler = () => setShowMenu(false)
    document.addEventListener("click", handler)
    return () => document.removeEventListener("click", handler)
  }, [showMenu])

  const handleDoubleClick = useCallback(() => {
    window.getSelection()?.removeAllRanges()
    setIsEditing(true)
  }, [])

  const handleSave = useCallback(() => {
    // Signal that a section just exited edit mode — click handlers
    // within the next 300ms should be suppressed to avoid opening tasks.
    ;(document as any).__sectionBlurAt = Date.now()
    setIsEditing(false)
    const id = task.id
    if (!id || !editRef.current) return
    const newTitle = (editRef.current.textContent || "").trim()
    if (newTitle && newTitle !== task.title) {
      updateTaskTitle(id, newTitle)
    }
  }, [task.id, task.title])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault()
        handleSave()
      }
      if (e.key === "Escape") {
        e.preventDefault()
        setIsEditing(false)
      }
    },
    [handleSave],
  )

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setMenuPos({ x: e.clientX, y: e.clientY })
    setShowMenu(true)
  }, [])

  const handleDelete = useCallback(() => {
    if (!task.id) return
    setShowMenu(false)
    deleteTask(task.id)
  }, [task.id])

  // Count unfinished children for the archive dialog
  const unfinishedCount = childIds?.length ?? 0

  const handleArchiveClick = useCallback(() => {
    setShowMenu(false)
    if (!task.id) return

    if (isArchived) {
      // Restore — no dialog needed
      archiveSection(task.id, false)
      return
    }

    if (unfinishedCount > 0) {
      // Has children — show dialog
      setShowArchiveDialog(true)
    } else {
      // No children — archive directly
      archiveSection(task.id, false)
    }
  }, [task.id, isArchived, unfinishedCount])

  const handleArchiveSectionOnly = useCallback(() => {
    if (!task.id) return
    setShowArchiveDialog(false)
    archiveSection(task.id, false)
  }, [task.id])

  const handleArchiveWithTasks = useCallback(() => {
    if (!task.id) return
    setShowArchiveDialog(false)
    archiveSection(task.id, true, childIds)
  }, [task.id, childIds])

  const handleAddTask = useCallback(async () => {
    if (!task.id || !task.parentPath) return
    setShowMenu(false)
    const result = await addTask({
      title: "",
      parentPath: task.parentPath,
      insertPosition: { afterId: task.id },
    })
    if (result.ok && onOpen) {
      requestEditFocus(result.data.id)
      onOpen(result.data.id)
    }
  }, [task.id, task.parentPath, onOpen, requestEditFocus])

  return (
    <div
      className={cn(
        "group relative",
        className,
      )}
      ref={
        dragHandleRef
          ? (node: HTMLDivElement | null) => {
              rowRef.current = node
              dragHandleRef(node)
            }
          : rowRef
      }
      {...(dragListeners
        ? (dragListeners as React.HTMLAttributes<HTMLDivElement>)
        : {})}
      style={dragHandleRef && !isMobile ? { touchAction: "none" } : undefined}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      data-section-row
    >
      {/* Content area — only this part gets the selection highlight */}
      <div
        className={cn(
          "relative flex items-center px-3",
          "py-2.5 transition-colors duration-150",
          dragHandleRef && "cursor-grab active:cursor-grabbing",
          isSelected && "bg-primary/10 rounded-lg",
        )}
      >
        {/* Bottom border — inset to align with checkboxes / icons. */}
        <div
          className={cn(
            "absolute bottom-0 left-3 right-3 h-px",
            "bg-foreground/16",
            isSelected && "opacity-0",
          )}
          data-section-border
        />
        {isEditing ? (
          <span
            ref={editRef}
            contentEditable
            suppressContentEditableWarning
            className={cn(
              "block flex-1 overflow-x-clip font-bold text-primary whitespace-nowrap",
              compact ? "text-sm leading-5" : "text-[20px] leading-6",
              "cursor-text outline-none",
            )}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
          >
            {task.title || ""}
          </span>
        ) : (
          <span
            className={cn(
              "block flex-1 overflow-x-clip font-bold text-ellipsis whitespace-nowrap select-none",
              compact ? "text-sm leading-5" : "text-[20px] leading-6",
              hasVisibleTitle(task.title) ? "text-primary" : "text-primary/50",
            )}
          >
            {getTitleWithFallback({ title: task.title, t, fallback: "section" })}
          </span>
        )}

        {/* More menu trigger – visible on hover */}
        <button
          className="ml-auto shrink-0 p-0 opacity-0 transition-opacity group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation()
            const rect = e.currentTarget.getBoundingClientRect()
            setMenuPos({ x: rect.left, y: rect.bottom + 4 })
            setShowMenu((prev) => !prev)
          }}
          aria-label={t("Section options")}
        >
          <DotsIcon className="size-6 text-foreground/40" />
        </button>

        {children}

        {/* Selection indicator — shown in selection mode with CSS transitions */}
        {isSelectionMode && (
          <button
            type="button"
            className="ml-2 flex shrink-0 items-center transition-[opacity,transform] duration-150 ease-out"
            style={{
              opacity: isSelected ? 1 : 0.3,
              transform: "scale(1)",
            }}
            onClick={(e) => { e.stopPropagation(); onSelect?.() }}
            aria-label={isSelected ? t("Deselect") : t("Select")}
          >
            <DynamicIcon
              type="project"
              percentage={isSelected ? 100 : 0}
              unfinished={0}
              status={ObjectStatus.Unfinished}
              size="small"
              color={isSelected ? "var(--primary)" : "currentColor"}
            />
          </button>
        )}

        {/* Context menu */}
        {showMenu && (
          <div
            ref={menuRef}
            className="fixed z-50 min-w-35 rounded-md border border-border bg-popover p-1 shadow-md"
            style={menuStyle}
          >
            {task.parentPath && (
              <>
                <button
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                  onClick={handleAddTask}
                  title={t("New task")}
                >
                  <PlusIcon className="size-4 text-foreground/50" />
                  {t("New task")}
                </button>
                <div className="my-1 h-px bg-foreground/5" />
              </>
            )}
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={handleArchiveClick}
            >
              <CheckIcon className="size-4 text-primary" />
              {isArchived ? t("Restore") : t("Archive")}
            </button>
            <button
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent"
              onClick={handleDelete}
            >
              <DeleteIcon className="size-4" />
              {t("Delete")}
            </button>
          </div>
        )}
      </div>

      {/* Section archive dialog */}
      <SectionArchiveDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        sectionTitle={task.title || ""}
        unfinishedCount={unfinishedCount}
        onArchiveSectionOnly={handleArchiveSectionOnly}
        onArchiveWithTasks={handleArchiveWithTasks}
      />
    </div>
  )
})
